
import {
  type Message,
  type SDK_Colors,
  type SDK_Filter,
  type SDK_Stack,
  strings,
} from "@quickpoint.me/shared";
import type { QpFile } from "@quickpoint.me/shared/src/gql/generated/schema";



const authorTransmitBC = (() => {
  try {
    const bc = new BroadcastChannel(strings.broadcastChannel.author);
    return bc;
  } catch {
    return false;
  }
})();

export const toUploaderBC = (() => {
  try {
    const bc = new BroadcastChannel(strings.broadcastChannel.toUploader);
    return bc;
  } catch {
    return false;
  }
})();

export const authorReceiveBC = (() => {
  try {
    const bc = new BroadcastChannel(strings.broadcastChannel.author);
    return bc;
  } catch {
    return false;
  }
})();

/**
 * Post a message to the viewer.
 * Used to pass updated code to the viewer
 * in the message function
 * @export
 * @param @link MessageType
 */
export function postMessageToViewer({
  hostId,
  type,
  action,
  payload,
}: Message): void {
  const viewerBC = (() => {
    try {
      const bc = new BroadcastChannel(strings.broadcastChannel.viewer);
      return bc;
    } catch {
      return false;
    }
  })();

  if (viewerBC) {
    const message = {
      hostId,
      type,
      action,
      payload: payload ? payload : null,
    };

    viewerBC.postMessage(message);
  }
}

/**
 * Back end Error messages from atoms and selectors that look for external data
 * should be sent with this function to be handled by the Messages module.
 * @param {string} error - The error message.
 * @export
 */
export function postErrorMessage(hostId: string, error: Message["error"]): void {
  if (authorTransmitBC) {
    const message: Message = {
      hostId,
      type: "QP",
      action: strings.messages.error,
      error: error,
    };
    authorTransmitBC.postMessage(message);
  }
}

/**
 *  Messages from within the application that can be used to trigger events.
 * @export
 * @param {string} action - The action to be taken
 */
export function postInternalApplicationAction(
  hostId: string,
  action: Message["action"],
  payload?: Message["payload"],
): void {
  if (authorTransmitBC) {
    const message: Message = {
      hostId,
      type: "QP",
      action,
      payload,
    };
    authorTransmitBC.postMessage(message);
  }
}

/**
 * Send a stack to the viewer
 * @export sendCodeToViewer
 */
export function sendCodeToViewer(hostId: string, stack: SDK_Stack): void {
  // the viewer expects a viewStack
  postMessageToViewer({
    hostId,
    type: "QP",
    action: strings.messages.edit,
    payload: stack,
  });
}
/**
 * Send a filter to the viewer
 * @export
 */
export function sendFilterToViewer(hostId: string, filter: SDK_Filter): void {
  postMessageToViewer({
    hostId,
    type: "QP",
    action: strings.messages.injectFilter,
    payload: filter,
  });
}

export function sendFilterNamesToViewer(
  hostId: string,
  filterNames: string[],
): void {
  postMessageToViewer({
    hostId,
    type: "QP",
    action: strings.messages.setFilterNames,
    payload: filterNames,
  });
}

/**
 * Ask the viewer to enter preview mode
 * @export
 */
export function preview(hostId: string): void {
  postMessageToViewer({ hostId, type: "QP", action: strings.messages.preview });
}

/**
 * Ask the viewer to exit preview mode
 * @export
 */
export function unPreview(hostId: string): void {
  postMessageToViewer({
    hostId,
    type: "QP",
    action: strings.messages.unPreview,
  });
}

export function enterPresentationMode(hostId: string, stack?: SDK_Stack): void {
  postMessageToViewer({
    hostId,
    type: "QP",
    action: strings.messages.presentationMode,
    payload: stack,
  });
}

export function exitPresentationMode(hostId: string): void {
  postMessageToViewer({
    hostId,
    type: "QP",
    action: strings.messages.exitPresentationMode,
  });
}

/**
 * Ask the viewer to enter preview mode
 * @export
 */
export function enterReadMode(hostId: string): void {
  postMessageToViewer({
    hostId,
    type: "QP",
    action: strings.messages.enterReadMode,
  });
}

/**
 * Ask the viewer to exit preview mode
 * @export
 */
export function exitReadMode(hostId: string): void {
  postMessageToViewer({
    hostId,
    type: "QP",
    action: strings.messages.exitReadMode,
  });
}

/**
 * Send all files to the fileUploader component
 * @export
 */
export function sendAllFiles(hostId: string, allFiles: QpFile[]): void {
  if (toUploaderBC) {
    const message: Message = {
      hostId,
      type: "QP",
      action: strings.messages.allFiles,
      payload: { files: allFiles },
    };
    toUploaderBC.postMessage(message);
  }
}

/**
 * Send all files to the fileUploader component
 * @export
 */
export function sendUploadFiles(message: Message): void {
  if (toUploaderBC) {
    toUploaderBC.postMessage(message);
  }
}

/**
 * Save the current code in the editor
 * @export
 */
export function saveStack(hostId: string, stack: SDK_Stack): void {
  postInternalApplicationAction(hostId, strings.messages.saveStack, stack);
}

export function toggleShowStacksDuringDemoMode(hostId: string): void {
  postInternalApplicationAction(
    hostId,
    strings.messages.toggleStoriesDuringDemoMode,
  );
}

export function updateFiles(hostId: string): void {
  postInternalApplicationAction(hostId, strings.messages.updateFiles);
}

export function deleteStack(hostId: string, fileName: string): void {
  postInternalApplicationAction(hostId, strings.messages.deleteStack, fileName);
}

export function sendCodeAndFilterToFileUploader(hostId: string, code: string, colors: SDK_Colors, stackId: string): void {
  if (toUploaderBC) {
    const message: Message = {
      hostId,
      type: "QP",
      action: strings.messages.codeAndFilter,
      payload: { code, colors, stackId },
    };
    toUploaderBC.postMessage(message);
  }
}

export function sendCodeToFileUploader(hostId: string, code: string): void {
  if (authorTransmitBC) {
    const message: Message = {
      hostId,
      type: "QP",
      action: "code",
      payload: code,
    };
    authorTransmitBC.postMessage(message);
  }
}
